import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createPropertyRequest,
  deletePropertyRequest,
  getPropertiesRequest,
  updatePropertyRequest,
} from "../../api/propertyApi";
import { CreatePropertyRequest } from "../../models/RequestModels/CreatePropertyRequest";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import {
  getApiErrorMessage,
  getApiErrorPayload,
} from "../../utils/helperFunctions/apiErrorHelper";

interface PropertyState {
  properties: PropertyResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: PropertyState = {
  properties: [],
  loading: false,
  error: null,
};

export const fetchProperties = createAsyncThunk(
  "properties/fetchProperties",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPropertiesRequest();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const createProperty = createAsyncThunk(
  "properties/createProperty",
  async (body: CreatePropertyRequest, { rejectWithValue }) => {
    try {
      const response = await createPropertyRequest(body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const updateProperty = createAsyncThunk(
  "properties/updateProperty",
  async (
    payload: { id: number; body: Partial<CreatePropertyRequest> },
    { rejectWithValue },
  ) => {
    try {
      const response = await updatePropertyRequest(payload.id, payload.body);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

export const deleteProperty = createAsyncThunk(
  "properties/deleteProperty",
  async (id: number, { rejectWithValue }) => {
    try {
      await deletePropertyRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(getApiErrorPayload(error));
    }
  },
);

const propertySlice = createSlice({
  name: "properties",
  initialState,
  reducers: {
    clearPropertyError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = action.payload;
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to load properties.",
        );
      })

      .addCase(createProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.loading = false;
        state.properties.unshift(action.payload);
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to create property.",
        );
      })

      .addCase(updateProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = state.properties.map((property) =>
          property.id === action.payload.id ? action.payload : property,
        );
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to update property.",
        );
      })

      .addCase(deleteProperty.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProperty.fulfilled, (state, action) => {
        state.loading = false;
        state.properties = state.properties.filter(
          (property) => property.id !== action.payload,
        );
      })
      .addCase(deleteProperty.rejected, (state, action) => {
        state.loading = false;
        state.error = getApiErrorMessage(
          action.payload,
          "Failed to delete property.",
        );
      });
  },
});

export const { clearPropertyError } = propertySlice.actions;

export default propertySlice.reducer;
